import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';

interface Venue {
  id: string;
  name: string;
  address: string;
  devices?: any[];
  createdAt: string;
}

const VenueManagement = () => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [formData, setFormData] = useState({ name: '', address: '' });
  const [message, setMessage] = useState('');

  const showMessage = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  }, []);

  const fetchVenues = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/venues');
      setVenues(response.data);
    } catch (error) {
      console.error('Error fetching venues:', error);
      showMessage('Načtení provozoven se nezdařilo', 'error');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  const handleCreateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/venues', formData);
      showMessage('Provozovna byla úspěšně vytvořena!', 'success');
      setFormData({ name: '', address: '' });
      setShowCreateForm(false);
      fetchVenues();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Provozovnu se nepodařilo vytvořit', 'error');
    }
  };

  const handleUpdateVenue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingVenue) return;

    try {
      await api.put(`/venues/${editingVenue.id}`, formData);
      showMessage('Provozovna byla úspěšně upravena!', 'success');
      setEditingVenue(null);
      setFormData({ name: '', address: '' });
      fetchVenues();
    } catch (error: any) {
      showMessage(error.response?.data?.error || 'Úpravu se nepodařilo uložit', 'error');
    }
  };

  const handleDeleteVenue = async (venueId: string) => {
    if (!window.confirm('Opravdu chcete tuto provozovnu smazat? Tuto akci nelze vrátit.')) {
      return;
    }

    try {
      await api.delete(`/venues/${venueId}`);
      showMessage('Provozovna byla smazána!', 'success');
      fetchVenues();
    } catch (error: any) {
      showMessage(
        error.response?.data?.error || 'Provozovnu se nepodařilo smazat. Může mít přiřazená zařízení.',
        'error'
      );
    }
  };

  const startEdit = (venue: Venue) => {
    setEditingVenue(venue);
    setFormData({ name: venue.name, address: venue.address });
    setShowCreateForm(false);
  };

  const cancelEdit = () => {
    setEditingVenue(null);
    setFormData({ name: '', address: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/admin"
              className="text-primary-600 hover:text-primary-700 font-semibold mb-2 inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Zpět na přehled
            </Link>
            <h1 className="text-5xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              🏢 Správa provozoven
            </h1>
          </div>
          <button
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setEditingVenue(null);
              setFormData({ name: '', address: '' });
            }}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-2xl font-semibold hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300"
          >
            {showCreateForm ? 'Zrušit' : '+ Vytvořit provozovnu'}
          </button>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-white/80 backdrop-blur-lg rounded-2xl shadow-card text-center">
            <p className="text-gray-800 font-semibold">{message}</p>
          </div>
        )}

        {(showCreateForm || editingVenue) && (
          <div className="mb-8 bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              {editingVenue ? 'Upravit provozovnu' : 'Vytvořit novou provozovnu'}
            </h2>
            <form onSubmit={editingVenue ? handleUpdateVenue : handleCreateVenue} className="space-y-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-2">Název provozovny</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="např. Downtown Arena"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-primary-500 focus:outline-none transition-all duration-300"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2">Adresa</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="např. Hlavní 123, Město"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-primary-500 focus:outline-none transition-all duration-300"
                  required
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 py-4 bg-gradient-to-r from-primary-600 to-secondary-600 text-white rounded-2xl font-bold hover:shadow-glow-lg transform hover:scale-105 transition-all duration-300"
                >
                  {editingVenue ? 'Uložit změny' : 'Vytvořit provozovnu'}
                </button>
                {editingVenue && (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-8 py-4 bg-gray-200 text-gray-700 rounded-2xl font-bold hover:bg-gray-300 transition-all duration-300"
                  >
                    Zrušit
                  </button>
                )}
              </div>
            </form>
          </div>
        )}

        <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-8 shadow-card">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Všechny provozovny ({venues.length})</h2>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Načítám provozovny...</p>
            </div>
          ) : venues.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-4xl mb-4">🏢</p>
              <p className="text-gray-600">Žádné provozovny nenalezeny. Vytvořte první!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {venues.map((venue) => (
                <div
                  key={venue.id}
                  className="p-6 border-2 border-gray-200 rounded-2xl hover:border-primary-300 hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {venue.name}
                      </h3>
                      <p className="text-gray-600 mb-3">
                        📍 {venue.address}
                      </p>
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full font-semibold">
                          {venue.devices?.length || 0} zařízení
                        </span>
                        <span className="text-gray-500">
                          Vytvořeno {new Date(venue.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => startEdit(venue)}
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-accent-500 to-accent-600 text-white rounded-xl font-semibold hover:shadow-glow transition-all duration-300"
                    >
                      ✏️ Upravit
                    </button>
                    <button
                      onClick={() => handleDeleteVenue(venue.id)}
                      className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all duration-300"
                    >
                      🗑️ Smazat
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueManagement;
